import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Snackbar, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { useSetupOnboardingStatus } from "../../hooks/useSetupOnboardingStatus";
import { useEffectivePermissions, toSet, canAny } from "../../hooks/useEffectivePermissions";
import useChatState from "./hooks/useChatState";
import useMessageHandler from "./hooks/useMessageHandler";
import { useRequests } from "../../hooks/useRequests";
import ChatLayout from "./ChatLayout";
import EmptyState from "./EmptyState";
import NewRequestModal from "./NewRequestModal";
import ApiErrorAlertBody, { resolveApiErrorNotice } from "../../components/ApiErrorAlertBody";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import {
  isPopupCallPath,
  isPopupServiceChatPath,
  isWorkspaceNavSearch,
} from "../../utils/teamNavigation";
import { openCallPopupWindow } from "../../utils/openCallPopupWindow";

const chatPageShellSx = {
  flex: 1,
  minHeight: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/* Skeleton que replica o layout do Chat (lista + área central) */
function ChatInitialSkeleton() {
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        gap: 1,
        p: 1,
        overflow: "hidden",
        backgroundImage: `linear-gradient(${alpha(t.palette.primary.main, 0.04)}, transparent)`,
      })}
    >
      {/* Painel esquerdo: lista de conversas */}
      <Box
        sx={{
          width: { xs: "100%", md: "25%" },
          minWidth: 280,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1, display: "flex", gap: 0.5, alignItems: "center", height: 62 }}>
          <Skeleton variant="rounded" width={40} height={40} />
          <Skeleton variant="rounded" width={40} height={40} />
          <Skeleton variant="rounded" width="60%" height={40} sx={{ flex: 1 }} />
        </Box>
        <Box sx={{ px: 1, pb: 1 }}>
          <Skeleton variant="text" width="90%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="85%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="92%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="78%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="88%" height={56} />
        </Box>
      </Box>
      {/* Área central: cabeçalho + mensagens */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="25%" sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Skeleton variant="rounded" width="70%" height={48} />
          <Skeleton variant="rounded" width="50%" height={48} sx={{ alignSelf: "flex-end" }} />
          <Skeleton variant="rounded" width="65%" height={48} />
          <Skeleton variant="rounded" width="55%" height={48} sx={{ alignSelf: "flex-end" }} />
        </Box>
      </Box>
    </Box>
  );
}

const Chat = () => {
  const { t } = useTranslation("chat");
  const { t: tOnb } = useTranslation("onboarding");
  const location = useLocation();
  const popupCallMode = useMemo(
    () => isPopupCallPath(location.pathname),
    [location.pathname]
  );
  const workspaceEmbedded = useMemo(
    () =>
      popupCallMode ||
      isPopupServiceChatPath(location.pathname) ||
      isWorkspaceNavSearch(location.search),
    [popupCallMode, location.pathname, location.search]
  );
  const { companyKey, isLoading: workspaceLoading } = useWorkspaceConfig();
  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const scopesSet = toSet(permData?.scopes);
  const canConfigureWorkspace = canAny(scopesSet, [
    ["companies", "view"],
    ["channels", "view"],
    ["users", "view"],
    ["types", "view"],
    ["automation_rules", "view"],
  ]);
  const {
    data: serverOnboarding,
    isLoading: serverOnboardingLoading,
    isError: serverOnboardingError,
  } = useSetupOnboardingStatus(
    companyKey,
    Boolean(companyKey && !workspaceLoading && !permsLoading)
  );

  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const {
    isMobile,
    userData,
    selectedConversationId,
    selectedConversation,
    showConversationList,
    showMessageHistory,
    showConversationDetails,
    isLoadingDetails,
    detailsAccessDenied,
    detailsDenied,
    initialLoading,
    isLoadingConversation,
    messagesSyncError,
    reloadConversationMessages,
    messages,
    conversations,
    conversationsTimeoutMessage,
    totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    advancedFilterState,
    applyChatFilterState,
    clearAdvancedFilterState,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    setSelectedConversation,
    setMessages,
    applyMessagesIfActive,
    handleSelectConversation,
    handleEndConversation,
    containerStyle,
    sendMessageMutation,
    isError,
    conversationsError,
    createConversation,
    isCreatingConversation,
    aiInsights,
    isLoadingInsights,
    aiInsightsError,
    loadAiInsights,
    hasMoreMessages,
    isLoadingMoreMessages,
    loadOlderMessages,
    refetchConversationDetails,
    handleDeleteMessage,
    handleReactMessage,
    handleEditWhatsappMessage,
    handleForwardWhatsappMessage,
    handleEditNote,
    handleDeleteNote,
    deletePersonalConversation,
  } = useChatState();

  const { updateMutation } = useRequests();

  const { handleSendMessage } = useMessageHandler({
    selectedConversation,
    userData,
    messages,
    setMessages,
    applyMessagesIfActive,
    sendMessageMutation,
    updateMutation,
    onUpdateConversation: (updatedFields) =>
      setSelectedConversation((prev) => (prev ? { ...prev, ...updatedFields } : prev)),
  });

  const onDeletePersonalConversation = async () => {
    try {
      await deletePersonalConversation();
      setSnackbar({
        open: true,
        message: t("pane.deletePersonalSuccess"),
        severity: "success",
      });
    } catch (err) {
      const raw =
        (typeof err?.message === "string" && err.message) ||
        (typeof err === "string" && err) ||
        (Array.isArray(err?.message) && err.message[0]) ||
        null;
      setSnackbar({
        open: true,
        message: raw || t("pane.deletePersonalFailed"),
        severity: "error",
      });
      throw err;
    }
  };

  const onReactMessage = async (messageId, emoji) => {
    try {
      await handleReactMessage(messageId, emoji);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Não foi possível reagir.",
        severity: "error",
      });
    }
  };

  const onEditWhatsappMessage = async (messageId, content) => {
    try {
      await handleEditWhatsappMessage(messageId, content);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Não foi possível editar.",
        severity: "error",
      });
    }
  };

  const onForwardWhatsappMessage = async (messageId, to) => {
    try {
      await handleForwardWhatsappMessage(messageId, to);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Não foi possível encaminhar.",
        severity: "error",
      });
    }
  };

  const onDeleteMessage = async (messageId) => {
    try {
      await handleDeleteMessage(messageId);
      setSnackbar({ open: true, message: t("page.messageDeleted"), severity: "success" });
    } catch (err) {
      const raw =
        (typeof err?.message === "string" && err.message) ||
        (typeof err === "string" && err) ||
        (Array.isArray(err?.message) && err.message[0]) ||
        null;
      setSnackbar({
        open: true,
        message: raw || t("page.messageDeleteFailed"),
        severity: "error",
      });
      throw err;
    }
  };

  const onEditNote = async (messageId, content) => {
    try {
      return await handleEditNote(messageId, content);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || t("messages.noteEditFailed", "Não foi possível editar a anotação."),
        severity: "error",
      });
      throw err;
    }
  };

  const onDeleteNote = async (messageId) => {
    try {
      return await handleDeleteNote(messageId);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || t("messages.noteDeleteFailed", "Não foi possível excluir a anotação."),
        severity: "error",
      });
      throw err;
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    action: null,
    severity: "error",
  });
  const [newRequestModalOpen, setNewRequestModalOpen] = useState(false);
  const navigate = useNavigate();

  // Mostra feedback visível quando falha o carregamento/sync do histórico.
  const lastMessagesErrKeyRef = React.useRef("");
  useEffect(() => {
    if (!messagesSyncError || detailsAccessDenied) return;
    const key = `${messagesSyncError.code || ""}::${messagesSyncError.message || ""}`;
    if (key && key === lastMessagesErrKeyRef.current) return;
    lastMessagesErrKeyRef.current = key;
    setSnackbar({
      open: true,
      message: messagesSyncError.message || "Não foi possível carregar o histórico da conversa.",
      severity: messagesSyncError.code === "errors.clients.duplicate" ? "warning" : "error",
    });
  }, [messagesSyncError, detailsAccessDenied]);

  // Abre a conversa quando navegado com state (legado) — preferir /chat/:id.
  useEffect(() => {
    const openId = location.state?.openConversationId;
    if (!openId || initialLoading) return;
    navigate(`/chat/${encodeURIComponent(String(openId))}`, { replace: true });
  }, [location.state?.openConversationId, initialLoading, navigate]);

  if (initialLoading) {
    return (
      <Box sx={chatPageShellSx}>
        <ChatInitialSkeleton />
      </Box>
    );
  }

  const showWorkspaceSetupPendingBanner =
    !serverOnboardingLoading &&
    !serverOnboardingError &&
    serverOnboarding?.needsSetup === true &&
    !canConfigureWorkspace;

  const hasConversations = Array.isArray(conversations) && conversations.length > 0;
  const dbTotal = typeof totalInDatabase === "number" ? totalInDatabase : null;
  // EmptyState só quando não há nenhuma conversa no banco e não estamos abrindo uma por URL/id.
  const showEmpty =
    !initialLoading &&
    !hasConversations &&
    dbTotal === 0 &&
    !selectedConversation &&
    selectedConversationId == null;
  const listIsFetching = Boolean(isFetching && hasConversations);
  const listIsFetchingNextPage = Boolean(isFetchingNextPage && hasConversations);

  const getProjectId = () => {
    const selected = getSelectedCompanyIds();
    return selected.length ? Number(selected[0]) : null;
  };

  async function handleNewConversation(payloadOverrides = {}) {
    try {
      const hasClient =
        (payloadOverrides.client && Object.keys(payloadOverrides.client).length > 0) ||
        payloadOverrides.clientId != null;
      if (!hasClient) {
        setSnackbar({
          open: true,
          message: t("page.selectClientForRequest"),
          severity: "warning",
        });
        return;
      }

      const projectId = getProjectId();
      const payload = {
        source: "chat",
        ...payloadOverrides,
        projectId: projectId || payloadOverrides.projectId || undefined,
        client: payloadOverrides.client ?? {},
        initialMessage: (payloadOverrides.initialMessage ?? "").trim(),
      };

      const created = await createConversation(payload);

      handleSelectConversation({
        id: created.id,
        name: created.name,
        lastMessage: created.lastMessage || "",
        timestamp: created.timestamp,
        channel: created.channel,
        status: created.status,
        assignedTo: created.assignedTo,
        unread: created.unread || 0,
        statusLabel: t("page.statusInProgress"),
      });
      setSnackbar({ open: true, message: t("page.requestCreated"), severity: "success" });
      return created;
    } catch (err) {
      console.error("Falha ao criar atendimento:", err);
      const notice = resolveApiErrorNotice(
        err,
        "Não foi possível criar o atendimento. Tente novamente."
      );
      setSnackbar({
        open: true,
        message: notice.message,
        action: notice.action,
        severity: "error",
      });
      throw err;
    }
  }

  if (showEmpty) {
    return (
      <Box sx={chatPageShellSx}>
        {showWorkspaceSetupPendingBanner ? (
          <Alert severity="info" sx={{ m: 1, mb: 0, flexShrink: 0 }}>
            {tOnb("setup.chatPendingAdmin")}
          </Alert>
        ) : null}
        <EmptyState
          userName={userData?.nome || userData?.name}
          onNewConversation={() => setNewRequestModalOpen(true)}
          isCreating={isCreatingConversation}
        />
        <NewRequestModal
          open={newRequestModalOpen}
          onClose={() => setNewRequestModalOpen(false)}
          onCreate={handleNewConversation}
          isCreating={isCreatingConversation}
          currentUser={userData}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.action ? 14000 : 6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            sx={{ maxWidth: 520, alignItems: "flex-start" }}
          >
            <ApiErrorAlertBody message={snackbar.message} action={snackbar.action} />
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={chatPageShellSx}>
      {showWorkspaceSetupPendingBanner ? (
        <Alert severity="info" sx={{ m: 1, mb: 0, flexShrink: 0 }}>
          {tOnb("setup.chatPendingAdmin")}
        </Alert>
      ) : null}
      <ChatLayout
        isMobile={isMobile}
        containerStyle={containerStyle}
        workspaceEmbedded={workspaceEmbedded}
        detailsPlacement={popupCallMode ? "left" : "right"}
        popupCallMode={popupCallMode}
        userData={userData}
        selectedConversationId={selectedConversationId}
        selectedConversation={selectedConversation}
        showConversationList={showConversationList}
        showMessageHistory={showMessageHistory}
        showConversationDetails={showConversationDetails}
        isLoadingDetails={isLoadingDetails}
        detailsAccessDenied={detailsAccessDenied}
        detailsDenied={detailsDenied}
        initialLoading={initialLoading}
        isLoadingConversation={isLoadingConversation}
        messagesSyncError={messagesSyncError}
        reloadConversationMessages={reloadConversationMessages}
        messages={messages}
        conversations={conversations}
        conversationsTimeoutMessage={conversationsTimeoutMessage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={listIsFetchingNextPage}
        isFetching={listIsFetching}
        refetchConversations={refetch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        advancedFilterState={advancedFilterState}
        applyChatFilterState={applyChatFilterState}
        clearAdvancedFilterState={clearAdvancedFilterState}
        onSelectConversation={handleSelectConversation}
        onSendMessage={handleSendMessage}
        onEndConversation={handleEndConversation}
        onUpdateConversation={(updatedFields) =>
          setSelectedConversation((prev) => ({ ...prev, ...updatedFields }))
        }
        onBack={() => {
          if (isMobile) {
            setShowConversationList(true);
            setShowMessageHistory(false);
            setShowConversationDetails(false);
            return;
          }
          setShowConversationList(true);
        }}
        onShowDetails={() => {
          if (isMobile) {
            setShowConversationDetails((prev) => {
              const next = !prev;
              // No mobile: quando abre detalhes, esconde mensagens;
              // quando fecha detalhes, volta a mostrar o histórico.
              setShowMessageHistory(!next);
              return next;
            });
          } else {
            setShowConversationDetails((prev) => !prev);
          }
        }}
        onOpenPopup={() => {
          openCallPopupWindow(selectedConversation?.id);
        }}
        onCloseConversationList={() => setShowConversationList(false)}
        onNewConversation={() => setNewRequestModalOpen(true)}
        isCreatingConversation={isCreatingConversation}
        isError={isError}
        conversationsError={conversationsError}
        aiInsights={aiInsights}
        isLoadingInsights={isLoadingInsights}
        aiInsightsError={aiInsightsError}
        onLoadAiInsights={loadAiInsights}
        hasMoreMessages={hasMoreMessages}
        isLoadingMoreMessages={isLoadingMoreMessages}
        onLoadMoreMessages={loadOlderMessages}
        onRefetchConversationDetails={refetchConversationDetails}
        onDeleteMessage={onDeleteMessage}
        onReactMessage={onReactMessage}
        onEditWhatsappMessage={onEditWhatsappMessage}
        onForwardWhatsappMessage={onForwardWhatsappMessage}
        onEditNote={onEditNote}
        onDeleteNote={onDeleteNote}
        onDeletePersonalConversation={onDeletePersonalConversation}
        totalInDatabase={totalInDatabase}
      />
      <NewRequestModal
        open={newRequestModalOpen}
        onClose={() => setNewRequestModalOpen(false)}
        onCreate={handleNewConversation}
        isCreating={isCreatingConversation}
        currentUser={userData}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.action ? 14000 : 6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ maxWidth: 520, alignItems: "flex-start" }}
        >
          <ApiErrorAlertBody message={snackbar.message} action={snackbar.action} />
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Chat;
